<template>
  <ZDialog v-model:open="isDialogOpen">
    <ZDialogContent

    >
      <ZDialogHeader>
        <ZDialogTitle>{{ $t('asset.command.image_export') }}</ZDialogTitle>
        <ZDialogDescription>
          {{ $t('asset.message.image_export') }}
        </ZDialogDescription>
      </ZDialogHeader>
      <template v-if="state == 'loading'">
        <IconAlert type="loading">
          {{ $t('common.action.loading') }}
        </IconAlert>
      </template>
      <template v-else-if="state == 'loaded'">
        <div class="flex gap-6 flex-col mt-0">
          <div class="md:px-4 py-2 flex justify-center">
            <ZDialogFooter class="py-4">
            <span class="flex justify-center align-center items-center" v-if="building">
              <svg
                class="animate-spin -ml-1 mr-3 h-10 w-10 text-center"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Trwa przygotowywanie archiwum ({{ progress }}%)</span>
            </span>
              <button v-on:click="download()" v-else class="inline-block p-2 px-4 rounded border">
                <span class="flex gap-2 items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" class="icon sm">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m.75 12 3 3m0 0 3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"></path>
                  </svg>
                  {{ $t('asset.command.download_images') }}
                </span>
              </button>
            </ZDialogFooter>
          </div>
        </div>
      </template>
      <template v-else-if="state == 'error'">
        <IconAlert type="error">
          {{ $t('common.errors.unexpected') }}
        </IconAlert>
      </template>
    </ZDialogContent>
  </ZDialog>
</template>

<script lang="ts">
import { defineComponent, inject, ref } from 'vue';
import Action from '@ui/Action.vue';
import {
  ZDialog,
  ZDialogContent,
  ZDialogDescription,
  ZDialogFooter,
  ZDialogHeader,
  ZDialogTitle,
} from '@shadcn/components/ui/dialog';
import IconAlert from '@ui/IconAlert.vue';
import { ComponentLoadState } from '@/types/ComponentLoadState';
import { ZTable, ZTableBody, ZTableCell, ZTableHead, ZTableHeader, ZTableRow } from '@shadcn/components/ui/table';
import { ValidationErrors } from '@/types/ValidationErrors';
import FormValidator from '@ui/FormValidator.vue';
import PrettyStatus from '@ui/PrettyStatus.vue';
import AssetTagsForm from '@ui/Assets/AssetTagsForm.vue';
import {assetImageExport, assetImageExportCommand} from "@/api/asset";
import {http} from "@/api";

type MinimalAssetData = App.Data.MinimalAssetData;
type AssetImageExportData = App.Data.AssetImageExportData;
type AssetImageExportRequest = App.Data.AssetImageExportRequest;

export default defineComponent({
  components: {
    AssetTagsForm,
    PrettyStatus,
    FormValidator,
    ZTableHeader,
    ZTableCell,
    ZTableHead,
    ZTableBody,
    ZTable,
    ZTableRow,
    IconAlert,
    Action,
    ZDialog,
    ZDialogContent,
    ZDialogDescription,
    ZDialogFooter,
    ZDialogHeader,
    ZDialogTitle,
  },

  props: {
    assets: {
      type: Array<MinimalAssetData>,
      required: true,
    },
  },

  emits: ['exportDialogClosed', 'saved'],

  setup() {
    const currentTab = ref('withinOrganization');

    return {
      currentTab,
      toast: inject('toast') as (props: any) => void,
    };
  },

  data() {
    return {
      isDialogOpen: true,
      state: 'loaded' as ComponentLoadState,
      errors: null as null | ValidationErrors,
      taskId: null as null | Number,
      poolId: null as null | any,
      building: true as boolean,
      downloadUrl: '#' as string,
      progress: '0' as string,
    };
  },

  computed: {
    canSubmit() {
      return true;
    },
  },

  watch: {
    isDialogOpen() {
      clearInterval(this.poolId);
      if (this.building) assetImageExportCommand(this.taskId, 'cancel').execute();

      this.$emit('exportDialogClosed', true);
    },
  },

  mounted() {
    const assetsArray = {
      asset_ids: this.assets.map(asset => asset.id?.toString())
    } as AssetImageExportRequest;
    assetImageExport(assetsArray).execute().then((response: AssetImageExportData) => {
      this.taskId = response.data.job_id;
      this.poolId = setInterval(this.check, 1000);
    });
  },

  methods: {
    check() {
      assetImageExportCommand(this.taskId, 'check').execute().then((response: AssetImageExportData) => {
        if (response.data.code == 'PENDING') {
          this.progress = response.data.payload;
        }

        if (response.data.code == 'DONE') {
          this.building = false;
          this.downloadUrl = response.data.payload;
          clearInterval(this.poolId);
        }
      });
    },

    download() {
      let contentType = '' as string;
      contentType = 'application/zip';

      http
        .get(this.downloadUrl, {
          responseType: 'blob',
        })
        .then(response => {
          const blob = new Blob([response.data], {
              type: contentType,
            }),
            url = window.URL.createObjectURL(blob);
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = response.headers['x-filename'];
          downloadLink.click();
        });

    }
  },
});
</script>
